// packages import
import { Box, Button, DialogActions, Grid, Typography } from '@mui/material'
import { FC, useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// component import
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
import { ConfirmCloseMatterModal } from './ConfirmCloseMatterModal'

// others
import { CaseStatus, GetTrustBalanceSummaryQuery, useUpdateCaseMutation } from '../../../../generated'
import { CANCEL, CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE, SOMETHING_WENT_WRONG } from '../../../constants'
import palette from '../../../theme/palette'
import { settlementCheckValidationSchema } from '../../../validationSchema'
import { CaseContext } from '../../../context'
import { formatCurrency } from '../../../utils'
import { printClientSettlementCheck } from '../../../../services'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'

interface SettlementCheckFormValues {
  checkNo: string
  checkDate: string
  memo?: string
}

interface TrustBalanceFormProps {
  trustBalanceSummary: GetTrustBalanceSummaryQuery['getTrustBalanceSummary']
  onClose: () => void
  onSuccess: () => void
}

const TrustBalanceForm: FC<TrustBalanceFormProps> = ({ trustBalanceSummary, onClose, onSuccess }) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState<SettlementCheckFormValues>({
    checkNo: '',
    checkDate: new Date().toISOString().split('T')[0],
    memo: ''
  })

  const { currentCase } = useContext(CaseContext)

  const { id: currentCaseId, caseNumber, consultMatterNumber } = currentCase || {}
  const clients = trustBalanceSummary?.ioltaBalanceSummary?.clients || []
  const retainerAccount = trustBalanceSummary?.ioltaBalanceSummary?.retainerAccount

  const methods = useForm<SettlementCheckFormValues>({
    mode: 'onChange',
    resolver: yupResolver(settlementCheckValidationSchema),
    defaultValues: {
      checkNo: '',
      checkDate: new Date().toISOString().split('T')[0],
      memo: ''
    }
  })

  const {
    handleSubmit,
    formState: { isValid }
  } = methods

  const [completeCase, { loading }] = useUpdateCaseMutation({
    variables: undefined,

    onError({ message }) {
      if (message === CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE) Alert.error(message)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    async onCompleted(data) {
      if (data) {
        const { updateCase } = data

        if (updateCase) {
          const { response, case: updatedCase } = updateCase
          const { status: responseStatus } = response || {}

          if (responseStatus === 200 && updatedCase) {
            Alert.success('Matter closed successfully! Generating settlement check(s)...')

            try {
              for (const client of clients) {
                await printClientSettlementCheck(updatedCase.id, client.clientId)
              }
            } catch (error) {
              Alert.error('Failed to generate check(s), please try again!')
            }
            handleCloseConfirmationModal()
            onSuccess()
          }
        }
      }
    }
  })

  const handleCloseCase = async () => {
    if (!currentCaseId) return

    await completeCase({
      variables: {
        updateCaseInput: {
          id: currentCaseId,
          status: CaseStatus.Completed,
          caseNumber: caseNumber || consultMatterNumber,
          settlementChecks: clients.map((client, index) => ({
            amount: client.trustBalance || 0,
            checkNo: formData.checkNo + index,
            date: formData.checkDate,
            clientId: client.clientId,
            memo: formData.memo
          }))
        }
      }
    })
  }

  const handleCloseConfirmationModal = () => {
    setShowConfirmation(false)
  }

  const onSubmit = async (formData: SettlementCheckFormValues) => {
    setFormData(formData)
    setShowConfirmation(true)
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            mt={3}
            p={2}
            sx={{
              backgroundColor: 'rgba(245, 245, 245, 0.3)',
              borderRadius: 1,
              border: '1px solid',
              borderColor: palette.lightGrayThree
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Unused Trust Balance
            </Typography>

            <Box sx={{ pl: 2, gap: 0.5, display: 'flex', flexDirection: 'column' }}>
              {clients.map(client => (
                <Box
                  key={client.clientId}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography fontSize="14px" color="text.secondary">
                    {client.name}
                  </Typography>
                  <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                    {formatCurrency(client.trustBalance || 0)}
                  </Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontSize="14px" color="text.secondary">
                  Account
                </Typography>
                <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                  {retainerAccount?.name}
                </Typography>
              </Box>
            </Box>

            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={2}>
                Settlement Check Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CustomController
                    controllerName="checkNo"
                    controllerLabel={clients.length > 1 ? `Starting Check No` : 'Check No'}
                    fieldType="text"
                    sx={{ my: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <CustomDatePickerController controllerName="checkDate" controllerLabel="Check Date" sx={{ my: 0 }} />
                </Grid>

                <Grid item xs={12}>
                  <CustomController
                    controllerName="memo"
                    controllerLabel="Memo"
                    fieldType="text"
                    isMultiLine
                    rowsLength={2}
                    sx={{ my: 0 }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <DialogActions sx={{ padding: '30px 0 0', flexWrap: 'wrap', gap: '10px' }}>
            <Button onClick={onClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button type="submit" disabled={!isValid} color="primary" variant="contained">
              Close Matter & Generate Client Check(s)
            </Button>
          </DialogActions>
        </form>
      </FormProvider>

      {showConfirmation && (
        <ConfirmCloseMatterModal
          isOpen={showConfirmation}
          clients={clients}
          loading={loading}
          onClose={handleCloseConfirmationModal}
          onConfirm={handleCloseCase}
        />
      )}
    </>
  )
}

export default TrustBalanceForm
